import _ from "lodash";

import { useCurrentOrderSet } from "@features/orders/orderSet/data/CurrentOrderSetContext";
import { BudgetWithoutSpend } from "@models/Budget";
import { OrderVariant } from "@models/OrderVariant";

import { useOrderSetBudgetSpend } from "./OrderSetBudgetSpendContext";
import { useCouponBudgetSpendQuery } from "./queries/budgetSpendQueries";

const budgetsOf = (variants: OrderVariant[]) =>
  _(variants)
    .flatMap((orderVariant) => orderVariant.budgets)
    .uniqBy("id")
    .value();

/**
 * The share of the order's spend this budget carries. Zero is a real answer,
 * so only an unparseable percentage falls back to the whole amount.
 */
const galloShareOf = (budget: BudgetWithoutSpend) => {
  const share = parseFloat(budget.galloPercentage);
  return Number.isNaN(share) ? 1 : share;
};

/**
 * What this order set does to the budgets it draws on: what each has left once
 * the order lands, and whether that is known yet.
 *
 * The order-set overview reads it twice, for the "Remaining After Order" column
 * and to decide whether the order can be submitted, so the figure the user sees
 * in red and the one that blocks Submit are the same number.
 */
const useOrderSetBudgetStatus = () => {
  const { orders, orderSetSpendPerBudget } = useCurrentOrderSet();
  const { budgetSpends, isPending, isFetching } = useOrderSetBudgetSpend();
  const {
    couponBudgetIds,
    isCouponSpendsUpToDate,
    getCouponRedemptionAndFees,
  } = useCouponBudgetSpendQuery();
  const couponBudgetIdSet = new Set(couponBudgetIds);

  const orderVariants = orders.flatMap((order) => order.orderVariants);

  /** Every budget the order set touches, which is what the table lists. */
  const budgets = budgetsOf(orderVariants);

  /**
   * Only the budgets this order actually spends from. A budget reached through
   * an item ordered at zero takes nothing from this order set, so an unrelated
   * order having already exhausted it is not this order's problem.
   */
  const orderedBudgets = budgetsOf(
    orderVariants.filter((orderVariant) => orderVariant.qty > 0)
  );

  /** Null until this budget's spend has loaded. */
  const remainingAfterOrder = (budget: BudgetWithoutSpend) => {
    const spend = budgetSpends?.[budget.id];
    if (!spend) return null;

    return (
      +spend.actualAvailableAmount -
      orderSetSpendPerBudget[budget.id] * galloShareOf(budget) -
      (getCouponRedemptionAndFees(budget.id) ?? 0)
    );
  };

  const overBudgetBudgets = orderedBudgets.filter((budget) => {
    const remaining = remainingAfterOrder(budget);
    return remaining !== null && remaining < 0;
  });

  /**
   * Leading with the budget count on purpose: the spend query is disabled when
   * there is nothing to ask about, so `isPending` never resolves for an order
   * set that draws on no budgets.
   */
  const isLoading =
    orderedBudgets.length > 0 &&
    (isPending ||
      // A refetch in flight over kept-previous data. The query is keyed by the
      // budget ids, so those figures can even belong to a different set of
      // budgets; present-but-stale would otherwise read as loaded.
      isFetching ||
      // A budget added since the last fetch is missing from the kept-previous
      // data even though the query is no longer pending.
      orderedBudgets.some((budget) => !budgetSpends?.[budget.id]) ||
      (couponBudgetIdSet.size > 0 && !isCouponSpendsUpToDate));

  /**
   * Coupon budgets this order spends from whose redemption and fee total is
   * settled but absent, which is what a failed request looks like. Treating
   * that as zero fees would understate the spend and could wave through an
   * order that is actually over budget.
   */
  const couponBudgetsMissingSpend = orderedBudgets.filter(
    (budget) =>
      couponBudgetIdSet.has(budget.id) &&
      getCouponRedemptionAndFees(budget.id) === null
  );

  return {
    budgets,
    remainingAfterOrder,
    overBudgetBudgets,
    isOverBudget: overBudgetBudgets.length > 0,
    isLoading,
    isCouponSpendUnavailable:
      !isLoading && couponBudgetsMissingSpend.length > 0,
  };
};

export default useOrderSetBudgetStatus;
